/** Typed API surface for the tickets feature — "what it knows" lives here, not
 *  in page components. Everything goes through apiFetch/apiSend (401 discipline). */
import { TEMPLATE_ACTOR } from '@itsm/shared';
import { apiFetch, apiSend } from '../../lib/api';
import type { ApprovalState, ResolvedField } from '@itsm/shared';

/** Step 46 — `task` joined the set. Named once here rather than repeated as a
 *  union in four places, which is how the fourth one gets missed. */
export type TicketType =
  | 'incident'
  | 'service_request'
  | 'task'
  | 'problem'
  | 'change'
  /** Step 54 — a TYPE, not a flag: it carries an outage record and a review
   *  that an incident does not. */
  | 'major_incident';

/**
 * The LIST a ticket of this type belongs to.
 *
 * Kai: a problem opened from `/problems` had a back arrow that went to
 * `/tickets`, which is not where they came from and not where the ticket
 * lives. Derived from the TYPE rather than from history on purpose — history
 * is wrong the moment somebody follows a link from an email, and "back" on a
 * problem should mean the problem list whichever door you came through.
 */
export function listPathForType(type: string): string {
  if (type === 'problem') return '/problems';
  if (type === 'change') return '/changes';
  return '/tickets';
}

/** What that list is called, for a back button that says where it goes. */
export function listLabelForType(type: string): string {
  if (type === 'problem') return 'problems';
  if (type === 'change') return 'changes';
  return 'the queue';
}

/**
 * The CANONICAL path for one ticket. Kai: *"the url should be /problems or
 * /changes etc"*.
 *
 * Every link in the app can keep saying `/tickets/<id>` — the detail page
 * rewrites the address once it knows the type. Correcting it in one place
 * beats chasing forty `navigate()` calls, and it also fixes links that are
 * already out in emails, where nobody can chase them at all.
 */
export function ticketPath(type: string, id: string, tab?: string | undefined): string {
  return `${listPathForType(type)}/${id}${tab ? `/${tab}` : ''}`;
}

export interface TicketRow {
  id: string;
  number: number;
  type: TicketType;
  subject: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: string | null;
  source: string;
  assigneeId: string | null;
  assigneeName?: string | null;
  groupId?: string | null;
  /** Resolved on the server — the queue never sees group ids on their own. */
  groupName?: string | null;
  catalogueItemId?: string | null;
  requester: { id: string; name: string | null; email: string };
  createdAt: number;
  updatedAt: number;
  /** Step 31 — the newest resolution clock, or null. */
  sla?: SlaClockRow | null;
  /** Step 48/49 — the planned window. On the TICKET because problems and
   *  changes share it. */
  plannedStartAt?: number | null;
  plannedEndAt?: number | null;
}

export interface StatusDef {
  key: string;
  name: string;
  category: 'open' | 'pending' | 'resolved' | 'closed';
}

export interface TicketList {
  rows: TicketRow[];
  total: number;
  cursor: string | null;
  facets: { status: Record<string, number>; priority: Record<string, number>; type: Record<string, number> };
  statuses: StatusDef[];
}

export interface Person {
  id: string;
  name: string | null;
  email: string;
  kind: string;
  /** Step 128 — directory detail, present on the ticket detail payload so the
   *  rail can show who asked without a second request. Optional because the
   *  people-picker endpoints return the narrow shape. */
  jobTitle?: string | null;
  department?: string | null;
  officeLocation?: string | null;
}

export interface TicketMessage {
  id: string;
  kind: 'requester_message' | 'public_reply' | 'private_note' | 'system';
  bodyHtml: string;
  bodyText: string;
  cc: string[] | null;
  bcc: string[] | null;
  mentions: Person[];
  createdAt: number;
  author: Person | null;
  /** Step 55 — who wrote it, so the browser can decide whether to offer the
   *  pencil using the same rule the server enforces. */
  authorId?: string | null;
  editedAt?: number | null;
  editedByName?: string | null;
}

export interface HoldInfo {
  reasonLabel: string;
  until: number | null;
  createdAt: number;
}

/** Step 31 — one SLA clock. The server sends an ABSOLUTE `dueAt`; the browser
 *  never derives "minutes remaining" (business hours make it non-linear). */
export interface SlaClockRow {
  id: string;
  kind: string;
  cycle: number;
  dueAt: number;
  pausedAt: number | null;
  achievedAt: number | null;
  breachedAt: number | null;
  targetMs: number;
}

export interface TicketDetail {
  activeHold: HoldInfo | null;
  slaClocks: SlaClockRow[];
  ticket: TicketRow & {
    statusCategory: string | null;
    group: { id: string; name: string } | null;
    requester: Person | null;
    assignee: Person | null;
    /** Step 72 — who filled it in, when that is not who it is for. */
    submitter?: Person | null;
    firstRespondedAt: number | null;
    resolvedAt: number | null;
    closedAt: number | null;
    fields?: Record<string, unknown> | null;
  };
  messages: TicketMessage[];
  statuses: StatusDef[];
  mergedInto?: string;
  links: { id: string; number: number; subject: string; type: TicketType }[];
  /** Step 119 — the parent, or null. One row, so it rides the detail payload;
   *  children are their own read because a parent can have many. */
  parent: { id: string; number: number; subject: string; type: TicketType } | null;
  /** Step 49 — present only on a change. The stepper needs both to grey out
   *  the transitions the API would refuse. */
  change?: {
    kind: string;
    justification: string | null;
    backoutPlan: string | null;
    testPlan: string | null;
    implementationPlan: string | null;
    impact: string | null;
    likelihood: string | null;
    risk: string | null;
    reviewOutcome: string | null;
    reviewNote: string | null;
  } | null;
  changeApproval?: ApprovalState;
  /** Step 195 — the board that insists on a decision, for the stepper's refusal. */
  changeMandatoryBoard?: string | null;
  /** Step 62 — which catalogue item this request came from, and the resolved
   *  definitions that label its stored answers. */
  catalogueItem?: { id: string; name: string; icon: string | null; description: string | null } | null;
  /** Step 44 — resolved to names on the server; a list of ids is a list
   *  nobody can read and every caller would need the same second request. */
  watchers?: {
    userId: string;
    addedBy: string | null;
    createdAt: number;
    name: string | null;
    email: string;
  }[];
  requestFields?: ResolvedField[];
}

export interface FilterableField {
  key: string;
  label: string;
  type: string;
  options: { value: string; label: string; archived?: boolean }[];
}

export interface CannedResponse {
  id: string;
  folder: string | null;
  title: string;
  bodyHtml: string;
}

/**
 * "Whoever is creating the ticket", as a template value — defined in
 * `@itsm/shared` so the schema that VALIDATES it and the editor that PRODUCES
 * it read the same constant. They did not, once, and the template silently
 * could not be saved.
 *
 * The same idea as the automation engine's `assign_to_actor`.
 */
export const ASSIGNEE_ACTOR = TEMPLATE_ACTOR;

export const ticketsApi = {
  // The reference's grouped omnisearch — top 3 per section + totals.
  // (`search` below is the merge-picker's quick ticket lookup — different job.)
  globalSearch: (q: string, entity?: string) =>
    apiFetch<{
      tickets: {
        entries: { id: string; number: number; type: string; subject: string }[];
        total: number;
      } | null;
      requesters: { entries: { id: string; name: string | null; email: string }[]; total: number } | null;
      agents: { entries: { id: string; name: string | null; email: string }[]; total: number } | null;
      items: {
        entries: { id: string; name: string; description: string | null; icon: string | null }[];
        total: number;
      } | null;
      articles: {
        entries: { id: string; title: string; slug: string; folderName: string }[];
        total: number;
      } | null;
    }>(`/api/tickets/search?q=${encodeURIComponent(q)}${entity ? `&entity=${entity}` : ''}`),
  list: (params: URLSearchParams) => apiFetch<TicketList>(`/api/tickets?${params.toString()}`),
  detail: (id: string) => apiFetch<TicketDetail>(`/api/tickets/${id}`),
  create: (body: {
    subject: string;
    type: string;
    priority: string;
    requesterEmail: string;
    requesterName?: string;
    bodyHtml: string;
    groupId?: string | null;
    assigneeId?: string | null;
    /** Step 46/119 — set when a task is raised FROM a ticket. Absent for a
     *  standalone task, which is the point: a task can belong to a ticket,
     *  and equally can not. */
    parentId?: string | null;
  }) => apiSend<{ id: string; number: number }>('/api/tickets', 'POST', body),
  patch: (id: string, body: Record<string, unknown>) => apiSend(`/api/tickets/${id}`, 'PATCH', body),
  patchFields: (id: string, values: Record<string, unknown>) =>
    apiSend<{ ok: true }>(`/api/tickets/${id}/fields`, 'PATCH', { values }),
  filterableFields: () => apiFetch<{ entries: FilterableField[] }>('/api/tickets/filterable-fields'),
  /** Step 149 — the layout the properties rail renders from. */
  propertiesLayout: () =>
    apiFetch<{ layoutId: string | null; fields: ResolvedField[] }>('/api/tickets/properties-layout'),
  addMessage: (
    id: string,
    body: {
      kind: 'public_reply' | 'private_note';
      bodyHtml: string;
      cc?: string[];
      bcc?: string[];
      mentions?: string[];
      attachmentIds?: string[];
      /** Step 154 — also write it onto every child and linked incident. */
      propagate?: boolean;
    },
  ) => apiSend<{ id: string; propagated: number }>(`/api/tickets/${id}/messages`, 'POST', body),
  /** Step 154 — the tickets a reply here would also reach, so the composer
   *  states the count the SERVER will act on rather than one assembled in the
   *  browser from whatever relationships it happened to have loaded. */
  downstream: (id: string) =>
    apiFetch<{ entries: { id: string; number: number; subject: string }[]; total: number }>(
      `/api/tickets/${id}/downstream`,
    ),
  draft: (id: string) =>
    apiFetch<{
      draft: {
        kind: 'public_reply' | 'private_note';
        bodyHtml: string;
        cc: string[];
        bcc: string[];
        mentions: Person[];
        updatedAt: number;
      } | null;
    }>(`/api/tickets/${id}/draft`),
  saveDraft: (
    id: string,
    body: {
      kind: 'public_reply' | 'private_note';
      bodyHtml: string;
      cc?: string[];
      bcc?: string[];
      mentions?: string[];
      expectedUpdatedAt: number | null;
    },
  ) => apiSend<{ ok: true; updatedAt: number }>(`/api/tickets/${id}/draft`, 'PUT', body),
  discardDraft: (id: string) => apiSend<{ ok: true }>(`/api/tickets/${id}/draft`, 'DELETE'),
  holdReasons: () =>
    apiFetch<{ entries: { id: string; label: string; endOnRequesterReply: boolean }[] }>(
      '/api/tickets/hold-reasons',
    ),
  hold: (id: string, body: { reasonId: string; until?: number }) =>
    apiSend(`/api/tickets/${id}/hold`, 'POST', body),
  // Step 44 — `userId` absent means "me", which is the overwhelmingly common
  // case and should not need the caller to know its own id.
  statuses: () =>
    apiFetch<{ entries: { key: string; name: string; category: string }[] }>('/api/tickets/statuses'),
  templates: () =>
    apiFetch<{
      entries: {
        id: string;
        name: string;
        description: string | null;
        prefill: Record<string, unknown>;
        useCount: number;
      }[];
    }>('/api/tickets/templates'),
  watch: (id: string, userId?: string) =>
    apiSend(`/api/tickets/${id}/watch`, 'POST', userId ? { userId } : {}),
  unwatch: (id: string, userId: string) => apiSend(`/api/tickets/${id}/watch/${userId}`, 'DELETE'),
  people: () => apiFetch<{ entries: Person[] }>('/api/org/people'),
  groups: () => apiFetch<{ entries: { id: string; name: string; memberIds: string[] }[] }>('/api/org/groups'),
  merge: (id: string, intoId: string) =>
    apiSend<{ ok: true; mergedInto: string }>(`/api/tickets/${id}/merge`, 'POST', { intoId }),
  link: (id: string, linkedId: string) => apiSend(`/api/tickets/${id}/links`, 'POST', { linkedId }),
  unlink: (id: string, linkedId: string) => apiSend(`/api/tickets/${id}/links/${linkedId}`, 'DELETE'),
  search: (q: string) =>
    apiFetch<TicketList>(`/api/tickets?${new URLSearchParams({ q, limit: '8' }).toString()}`),
};

export const cannedResponsesApi = {
  list: () => apiFetch<{ entries: CannedResponse[] }>('/api/tickets/canned-responses'),
  create: (body: { folder?: string; title: string; bodyHtml: string }) =>
    apiSend<{ id: string }>('/api/tickets/canned-responses', 'POST', body),
  update: (id: string, body: { folder?: string; title?: string; bodyHtml?: string }) =>
    apiSend(`/api/tickets/canned-responses/${id}`, 'PATCH', body),
  remove: (id: string) => apiSend(`/api/tickets/canned-responses/${id}`, 'DELETE'),
};

export const PRIORITY_META: Record<string, { label: string; dot: string }> = {
  // The dot is the row's ONLY saturated colour besides the status badge —
  // weight and muted text carry everything else ("modern", not "Christmas tree").
  urgent: { label: 'Urgent', dot: 'bg-red-500' },
  high: { label: 'High', dot: 'bg-orange-500' },
  medium: { label: 'Medium', dot: 'bg-blue-400' },
  low: { label: 'Low', dot: 'bg-slate-400' },
};

export const STATUS_BADGE: Record<string, string> = {
  open: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  on_hold: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  resolved: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  closed: 'bg-muted text-muted-foreground',
};
